/**
 * feat-298 — the stable keys every section on `/quiz-review` is addressed by.
 *
 * Lesson and Drill keys ARE the feat-284 `data-item-key` values (`u{id}`,
 * `u{id}-l{id}`) — do not change their format; the language-switch scroll
 * anchor depends on it. The same strings key the body's collapse state, the TOC
 * jump targets (`data-nav-target`) and the scroll-spy.
 *
 * A Lesson renders in full exactly once on the page (see groupByTrack), so
 * `u{id}` is unique even when the Lesson sits in several Tracks.
 */
import { QuizUnit } from '../../../services/quizContentAdminService';
import { GroupedCurriculum, UNASSIGNED_GROUP_ID } from './groupByTrack';

export const trackKey = (groupId: string): string =>
  groupId === UNASSIGNED_GROUP_ID ? 't-none' : `t${groupId}`;

export const lessonKey = (lessonId: string): string => `u${lessonId}`;

export const drillKey = (lessonId: string, drillId: string): string =>
  `${lessonKey(lessonId)}-l${drillId}`;

/** Attribute on every Track header, Lesson banner and Drill banner: the element
 *  a jump scrolls to and the scroll-spy observes. */
export const NAV_TARGET_ATTR = 'data-nav-target';

/** Find a rendered nav target by key. Iterates rather than building a selector
 *  so no id ever has to be CSS-escaped. */
export function findNavTarget(root: ParentNode | null | undefined, key: string): HTMLElement | null {
  if (!root) return null;
  const nodes = root.querySelectorAll<HTMLElement>(`[${NAV_TARGET_ATTR}]`);
  for (let i = 0; i < nodes.length; i += 1) {
    if (nodes[i].getAttribute(NAV_TARGET_ATTR) === key) return nodes[i];
  }
  return null;
}

export interface SectionIndex {
  /** Section key → the keys of its body ancestors, outermost first. A jump
   *  expands all of them, plus the target itself. */
  ancestors: Map<string, string[]>;
  /** Every Lesson and Drill key — what "Collapse all" collapses. Tracks stay
   *  open so the reader gets the list of Lesson banners (NOTES §9). */
  belowTrack: string[];
}

/** Index the rendered sections of a grouped curriculum. Only FULL Lesson
 *  renderings are indexed: a stub is a pointer, never a section. */
export function indexSections(grouped: GroupedCurriculum): SectionIndex {
  const ancestors = new Map<string, string[]>();
  const belowTrack: string[] = [];
  const addLesson = (u: QuizUnit, parents: string[]) => {
    const lk = lessonKey(u.id);
    ancestors.set(lk, parents);
    belowTrack.push(lk);
    for (const d of u.drills) {
      const dk = drillKey(u.id, d.id);
      ancestors.set(dk, [...parents, lk]);
      belowTrack.push(dk);
    }
  };
  if (grouped.mode === 'flat') {
    grouped.lessons.forEach((u) => addLesson(u, []));
  } else {
    for (const g of grouped.groups) {
      const tk = trackKey(g.id);
      ancestors.set(tk, []);
      for (const e of g.entries) if (e.kind === 'full') addLesson(e.lesson, [tk]);
    }
  }
  return { ancestors, belowTrack };
}
